import { forEachParentNode, isCreativeNode, isGroupDataNode, toFlatNodeList } from './helpers';
export const MAX_NESTED_GROUPS = 7;
export function isLockedAndDirectlySelected(node, selection) {
    const isSelectionEmpty = selection.nodes.length === 0;
    const isNodeDirectlySelected = selection.nodes.includes(node);
    const isAnyAncestorLockedAndDirectlySelected = getLockedAncestors(node).some(ancestor => selection.nodes.includes(ancestor));
    if (!isSelectionEmpty && (isNodeDirectlySelected || isAnyAncestorLockedAndDirectlySelected)) {
        return isLocked(node);
    }
    else {
        return false;
    }
}
export function isLocked(node) {
    return node.locked || hasLockedAncestor(node);
}
export function hasLockedAncestor(node) {
    let hasLockedParent = false;
    forEachParentNode(node, parent => {
        if (parent.locked) {
            hasLockedParent = true;
            return true;
        }
    });
    return hasLockedParent;
}
export function getLockedAncestors(node) {
    const lockedParents = [];
    forEachParentNode(node, parent => {
        if (parent.locked) {
            lockedParents.push(parent);
        }
    });
    return lockedParents;
}
/**
 * This function finds the id of the closest hidden ancestor of the node
 * @param node node to check for hidden ancestor
 * @returns closest hidden ancestor id or undefined if no hidden ancestor is found
 */
export function getHiddenAncestorId(node) {
    let hiddenParentId;
    forEachParentNode(node, parent => {
        if (parent.hidden) {
            hiddenParentId = parent.id;
            return true;
        }
    });
    return hiddenParentId;
}
/**
 * Filter out nodes that are part of an included group
 */
export function filterAncestorNodes(nodeList) {
    return nodeList.filter(node => !node.__parentNode || !nodeList.includes(node.__parentNode));
}
/**
 * Looks for the first expanded ancestor of node
 */
export function findExpandedAncestor(node, nodeList) {
    let ancestor;
    forEachParentNode(node, parent => {
        const isParentExpanded = nodeList.some(tl => { var _a; return ((_a = tl.node.__parentNode) === null || _a === void 0 ? void 0 : _a.id) === parent.id && !tl.collapsed; });
        if (isGroupDataNode(parent) && isParentExpanded) {
            ancestor = parent;
            return true;
        }
    });
    return ancestor;
}
/**
 * Checks if parent of node is selected
 */
export function isParentNodeSelected(node, selection) {
    return selection.nodes.some(n => { var _a; return isGroupDataNode(n) && ((_a = node.__parentNode) === null || _a === void 0 ? void 0 : _a.id) === n.id; });
}
export function hasSelectionOfAllNodesInGroup(selection) {
    const { elements, nodes } = selection;
    return nodes.every(node => {
        elements.every(element => {
            if (isGroupDataNode(node)) {
                return node.findNodeById_m(element.id);
            }
            return element.id === node.id;
        });
    });
}
export function getBoundingBoxOfGroup(node) {
    let x1;
    let y1;
    let x2;
    let y2;
    node.elements.forEach(element => {
        if (x1 === undefined || element.x < x1) {
            x1 = element.x;
        }
        if (y1 === undefined || element.y < y1) {
            y1 = element.y;
        }
        if (x2 === undefined || element.x + element.width > x2) {
            x2 = element.x + element.width;
        }
        if (y2 === undefined || element.y + element.height > y2) {
            y2 = element.y + element.height;
        }
    });
    return {
        x: x1,
        y: y1,
        width: x2 - x1,
        height: y2 - y1
    };
}
export function getSizeAndPositionOfNode(node) {
    if (isGroupDataNode(node)) {
        const box = getBoundingBoxOfGroup(node);
        const { x, y, width, height } = box;
        return { x, y, width, height };
    }
    else {
        const { x, y, width, height } = node;
        return { x, y, width, height };
    }
}
export function getIndexOfNodeInParent(node) {
    const parent = node.__parentNode || node.__rootNode;
    if (!parent) {
        throw new Error('Failed to get index of node. Node has no parent.');
    }
    const index = toFlatNodeList(parent).findIndex(({ id }) => id === node.id);
    if (index === -1) {
        throw new Error('Failed to get index of node. Node is not part of parent.');
    }
    return index;
}
export function getCommonParent(nodes, creativeDocument) {
    var _a;
    const creativeNodes = toFlatNodeList(creativeDocument).reverse();
    const parentGroup = (_a = creativeNodes.find(node => nodes.some(n => n.id === node.id))) === null || _a === void 0 ? void 0 : _a.__parentNode;
    const nodeInRoot = nodes.some(n => !n.__parentNode);
    const commonParent = nodeInRoot ? creativeDocument : parentGroup || creativeDocument;
    return commonParent;
}
export function canCreateGroup(nodes, creativeDocument) {
    const descendantLevels = getSelectionDepth(nodes);
    const commonParent = getCommonParent(nodes, creativeDocument);
    const nestingLevel = isCreativeNode(commonParent) ? 0 : getNestingLevel(commonParent);
    return nestingLevel + descendantLevels < MAX_NESTED_GROUPS;
}
export function canMoveSelectionIntoGroup(nodes, target) {
    if (isCreativeNode(target)) {
        return true;
    }
    const selectionDepth = getSelectionDepth(nodes);
    const targetLevel = getNestingLevel(target);
    return selectionDepth + targetLevel <= MAX_NESTED_GROUPS;
}
export function getNestingLevel(node) {
    let parent = node.__parentNode;
    let parentAmount = isGroupDataNode(node) ? 1 : 0;
    while (parent && isGroupDataNode(parent) && parent !== parent.__parentNode) {
        parent = parent.__parentNode;
        parentAmount++;
    }
    return parentAmount;
}
export function getSelectionDepth(nodes) {
    if (nodes.length === 0) {
        return 0;
    }
    const depths = nodes.map(node => (isGroupDataNode(node) ? getGroupDepth(node) : 0));
    return Math.max(...depths);
}
export function getGroupDepth(group, isRoot = true) {
    const childGroupsDepths = group.nodes.map(node => {
        if (isGroupDataNode(node) || isCreativeNode(node)) {
            return 1 + getGroupDepth(node, false);
        }
        else {
            return 0;
        }
    });
    const maxChildDepth = Math.max(...childGroupsDepths);
    return isRoot ? maxChildDepth + 1 : maxChildDepth;
}
